import {
  useState,
  useEffect,
  useMemo,
  FC,
  Dispatch,
  SetStateAction,
} from 'react';

import { useFormikContext } from 'formik';
import { capitalize, compact } from 'lodash';
import {
  CreateSimpleCompanyModal,
  CreateSimpleLeadModal,
} from 'modules/account/contact/components';
import {
  useGetAccountUsers,
  useGetCompanies,
  useGetLeads,
} from 'modules/account/contact/hooks';
import { Input } from 'ui/components';
import { Currency, DateField, Select, TagsField } from 'ui/components/form';
import { AvatarOption } from 'ui/components/form/SelectAvatar/SelectAvatar';
import SelectAvatarPopover from 'ui/components/form/SelectAvatar/SelectAvatarPopover';
import { OPTIONS_PROPS } from 'ui/components/nav/Dropdown/Dropdown';
import { useExternalModal } from 'ui/partials/Modal/useModal';
import { SIZE } from 'utils/constants';

import { useGetPipelines, useGetStages } from '../hooks';
import { DEAL_FORM } from './schema';

type DealInfoFormProps = {
  setForm: Dispatch<SetStateAction<any>>;
};

const DealInfoForm: FC<DealInfoFormProps> = ({ setForm }) => {
  const { values, touched, errors, handleBlur, isSubmitting, setFieldValue } =
    useFormikContext<DEAL_FORM>();

  const [accountUserKeyword, setAccountUserKeyword] = useState('');
  const [companyKeyword, setCompanyKeyword] = useState('');
  const [leadKeyword, setLeadKeyword] = useState('');

  const { data: accountUsers } = useGetAccountUsers({
    keyword: accountUserKeyword,
  });

  const { data: pipelines } = useGetPipelines();
  const { data: stages } = useGetStages(values.pipelineId);
  const { data: companies } = useGetCompanies({ keyword: companyKeyword });
  const { data: leads } = useGetLeads({ keyword: leadKeyword });

  const accountUserItems = useMemo(
    () =>
      accountUsers?.data?.map((accountUser) => ({
        photoUrl: accountUser.photoUrl,
        itemLabel: accountUser.fullName,
        description: accountUser.emailAddress,
        value: accountUser._id,
      })) ?? ([] as AvatarOption[]),
    [accountUsers],
  );

  const defaultAccountUsers = useMemo(
    () =>
      accountUserItems?.filter((i) => values.accountUserId?.includes(i.value)),
    [accountUserItems, values.accountUserId],
  );

  const companyItems = useMemo(
    () =>
      companies?.data?.map((comp) => ({
        photoUrl: comp.photoUrl,
        itemLabel: comp.name,
        value: comp.id,
        description: capitalize(comp.industry),
      })) ?? ([] as AvatarOption[]),
    [companies],
  );

  const defaultCompanyId = useMemo(
    () => companyItems?.find((comp) => comp.value === values.companyId),
    [companyItems, values.companyId],
  );

  const contactItems = useMemo(
    () =>
      leads?.data.map((lead) => ({
        photoUrl: lead.photoUrl,
        itemLabel: lead.fullName,
        description: lead.emailAddress,
        value: lead.id,
      })) ?? ([] as AvatarOption[]),
    [leads],
  );

  const defaultContacts = useMemo(
    () => contactItems?.filter((i) => values.leadId?.includes(i.value)),
    [contactItems, values.leadId],
  );

  const { showModal } = useExternalModal();

  const handleToggleCompany = () => {
    showModal({
      title: 'Create Company',
      disableOutsideClick: true,
      content: <CreateSimpleCompanyModal setForm={setForm} />,
      size: SIZE.Small,
    });
  };

  const handleToggleLead = () => {
    showModal({
      title: 'Create Contact',
      disableOutsideClick: true,
      content: <CreateSimpleLeadModal setForm={setForm} />,
      size: SIZE.Small,
    });
  };

  useEffect(() => {
    if (stages?.length && !values.stageId) {
      setFieldValue('stageId', stages[0]?._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const stageOptions = useMemo(
    () =>
      stages?.map((stage) => ({
        value: stage?._id,
        label: stage.name,
      })) ?? [],
    [stages],
  );

  const pipelineOptions = useMemo(
    () =>
      pipelines?.map((pipeline) => ({
        value: pipeline?._id,
        label: pipeline.name,
      })) ?? [],
    [pipelines],
  );

  return (
    <div className="flex flex-col gap-y-4 pt-7">
      <div>
        <Input
          className="w-full"
          type="text"
          name="name"
          size={SIZE.Small}
          textType={errors.name && touched.name ? 'danger' : 'default'}
          onChange={(e) => setFieldValue(e.target.name, e.target.value)}
          onBlur={handleBlur}
          value={values.name}
          placeholder="Enter Deal Name"
          subText={errors.name && touched.name ? errors.name : ''}
          label="Deal Name"
          disabled={isSubmitting}
          required
        />
      </div>
      <div className="flex gap-x-[34px]">
        <Select
          required
          label="Pipeline"
          value={
            pipelineOptions?.find(
              (pipeline) => pipeline.value === values.pipelineId,
            ) as unknown as string
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('pipelineId', val.value);
          }}
          placeholder="Select pipeline"
          className="flex-1"
          disabled={isSubmitting}
          name="pipelineId"
          options={pipelineOptions}
          textType={
            errors.pipelineId && touched.pipelineId ? 'danger' : 'default'
          }
          subText={
            errors.pipelineId && touched.pipelineId
              ? errors.pipelineId
              : undefined
          }
        />
        <Select
          required
          label="Stage"
          value={
            stageOptions?.find(
              (stage) => stage.value === values.stageId,
            ) as unknown as string
          }
          onChange={(val: OPTIONS_PROPS) => {
            setFieldValue('stageId', val.value);
          }}
          placeholder="Select stage"
          className="flex-1"
          disabled={isSubmitting}
          name="stageId"
          options={stageOptions}
          textType={errors.stageId && touched.stageId ? 'danger' : 'default'}
          subText={
            errors.stageId && touched.stageId ? errors.stageId : undefined
          }
        />
      </div>
      <div className="flex gap-x-[34px]">
        <Currency
          className="flex-1"
          name="amount"
          size={SIZE.Small}
          onValueChange={(e) => setFieldValue('amount', e)}
          onBlur={handleBlur}
          value={values.amount ?? 0}
          placeholder="Input amount"
          subText={errors.amount && touched.amount ? errors.amount : undefined}
          textType={errors.amount && touched.amount ? 'danger' : 'default'}
          label="Amount"
          disabled={isSubmitting}
        />
        <DateField
          className="flex-1"
          selected={values.closedAt ? new Date(values.closedAt) : null}
          onChange={(_, value) =>
            setFieldValue('closedAt', new Date(value as Date))
          }
          placeholder="Select"
          name="closedAt"
          size={SIZE.Small}
          label="Closed Date"
          disabled={isSubmitting}
        />
      </div>
      <div>
        <Select
          className="w-full"
          textType={errors.type && touched.type ? 'danger' : 'default'}
          subText={errors.type && touched.type ? errors.type : undefined}
          label="Deal Type"
          onChange={(val) => setFieldValue('type', val.value)}
          options={[
            {
              value: 'NEW_BUSINESS',
              label: 'New Business',
            },
            {
              value: 'EXISTING_BUSINESS',
              label: 'Existing Business',
            },
          ]}
          size={SIZE.Small}
          name="type"
          placeholder="Select"
          disabled={isSubmitting}
          defaultValue={values.type}
          required
        />
      </div>
      <div>
        <SelectAvatarPopover
          {...(errors.accountUserId && { state: 'danger' })}
          label="Deal Owner"
          subText={
            errors.accountUserId && touched.accountUserId
              ? errors.accountUserId
              : undefined
          }
          selected={defaultAccountUsers}
          size={SIZE.Small}
          values={accountUserItems}
          onInputChange={(val) => setAccountUserKeyword(val)}
          onSelect={(val) => {
            setFieldValue('accountUserId', values.accountUserId?.concat(val));
          }}
          onClickSelected={(selected) =>
            setFieldValue(
              'accountUserId',
              values.accountUserId?.filter((value) => value !== selected),
            )
          }
          onClear={() => setFieldValue('accountUserId', [])}
          placeholder="Select Deal Owner"
        />
      </div>
      <div>
        <TagsField
          size={SIZE.Small}
          className="w-full"
          onChange={(val) =>
            setFieldValue(
              'tagId',
              val?.map((tag) => tag.value),
            )
          }
          defaultValue={compact(values.tagId)}
        />
      </div>
      <p className="border-b border-[#DCECF8] py-3.5 text-sm text-content">
        Associated Deal With
      </p>
      <SelectAvatarPopover
        {...(errors.companyId && { state: 'danger' })}
        label="Company"
        subText={
          errors.companyId && touched.companyId ? errors.companyId : undefined
        }
        selected={compact([defaultCompanyId as AvatarOption])}
        size={SIZE.Small}
        values={companyItems}
        onInputChange={(val) => setCompanyKeyword(val)} // when searching
        onSelect={(val) => setFieldValue('companyId', val)} // when selecting from options
        onClickSelected={() => setFieldValue('companyId', null)} // when clicking a selected
        onClear={() => setFieldValue('companyId', null)} // when clicking X button
        isSingleSelect
        onClickCreate={handleToggleCompany}
        placeholder="Select company"
      />
      <SelectAvatarPopover
        {...(errors.leadId && { state: 'danger' })}
        label="Leads"
        subText={errors.leadId && touched.leadId ? errors.leadId : undefined}
        selected={defaultContacts}
        size={SIZE.Small}
        values={contactItems}
        onInputChange={(val) => setLeadKeyword(val)}
        onSelect={(val) => setFieldValue('leadId', values.leadId?.concat(val))}
        onClickSelected={(selected) =>
          setFieldValue(
            'leadId',
            values.leadId?.filter((value) => value !== selected),
          )
        }
        onClear={() => setFieldValue('leadId', [])}
        onClickCreate={handleToggleLead}
        placeholder="Select leads"
      />
    </div>
  );
};

export default DealInfoForm;
